import React, { useEffect, useRef } from 'react'

import * as d3 from 'd3'

import * as style from './data-slide.module.scss'

const DataGraph = ({ data, title, barColor, inView }) => {
  const ref = useRef(null)

  useEffect(() => {
    const config = {
      width: 1080,
      height: 520
    }

    const margin = { top: 24, right: 80, bottom: 100, left: 100 },
      width = config.width - margin.left - margin.right,
      height = config.height - margin.top - margin.bottom

    const svgContainer = d3.select(ref.current)

    const p2Value = data[0]
    const growthValue = data[1]

    svgContainer.selectAll('*').remove()

    // Declare the x (horizontal position) scale.
    const x = d3
      .scaleBand()
      .domain(p2Value.values.map((d) => d.label))
      .range([margin?.left, width - margin?.right])
      .paddingInner([0.2])

    // Declare the y (vertical position) scale.
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(p2Value.values, (d) => d.value)])
      .range([height - margin?.bottom, margin?.top])

    const y2 = d3
      .scaleLinear()
      .domain([0, d3.max(growthValue.values, (d) => d.value)])
      .range([height - margin.bottom, margin.top])

    const locale = d3.formatLocale({
      thousands: '.',
      grouping: [3],
      currency: ['$', '']
    })

    // Create the SVG container.
    const svg = svgContainer
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto;')

    // Add the x-axis and label.
    svg
      .append('g')
      .attr('transform', `translate(0,${height - margin?.bottom})`)
      .call(
        d3
          .axisBottom(x)
          .tickSizeOuter(0)
          .tickSize(0)
          .tickPadding(10)
          .tickFormat('')
      )
      .call((g) => g.select('.domain').remove())
      .attr('class', style.barScatterXAxis)
      .selectAll('text')
      .selectAll('tspan')
      .data(function (d) {
        if (!d?.length) {
          return ''
        }
        return d.split(' ')
      })
      .enter()
      .append('tspan')
      .attr('x', 0)
      .attr('dy', function (d, i) {
        return (i + 1) * 0.75 + 'em'
      })
      .text(String)

    // Add the y-axis and label, and remove the domain line.
    svg
      .append('g')
      .attr('transform', `translate(${margin?.left},0)`)
      .call(
        d3
          .axisLeft(y)
          .ticks(p2Value.values.length)
          .tickFormat(locale.format('$,'))
          .tickSize(0)
          .tickPadding(5)
      )
      .call((g) => g.select('.domain').remove())
      .attr('class', style.barScatterYAxis)

    svg
      .append('g')
      .attr('transform', `translate(${width - margin.right},0)`)
      .call(
        d3
          .axisRight(y2)
          .ticks(8)
          .tickSize(0)
          .tickPadding(8)
          .tickFormat((d) => `${d}%`)
      )
      .call((g) => g.select('.domain').remove())
      .attr('class', style.barScatterYAxis)

    // Add the y-axis grid lines.
    svg
      .selectAll('line.grid')
      .data(y.ticks(8))
      .enter()
      .append('line')
      .attr('class', style.gridLine)
      .attr('x1', margin?.left)
      .attr('x2', width - margin.right)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))

    // Add a rect for each bar.
    svg
      .append('g')
      .attr('fill', barColor ?? 'var(--complementary-second-150)')
      .selectAll()
      .data(p2Value.values)
      .join('rect')
      .attr('x', (d) => x(d.label) + 16)
      .attr('y', (d) => y(0))
      .attr('height', (d) => 0)
      .attr('width', x.bandwidth() - 32)

    svg
      .append('g')
      .selectAll('text')
      .data(growthValue.values)
      .enter()
      .append('text')
      .text((d) => Number(d.value / 100).toFixed(2) ?? '')
      .attr('y', (d) => y2(d.value) + 4)
      .attr('x', (d, i) =>
        growthValue.values.length - 1 === i
          ? x(d.label) + x.bandwidth() / 1.5 - 28
          : x(d.label) + x.bandwidth() / 1.5 + 24
      )
      .attr('text-anchor', 'middle')
      .attr('fill', 'var(--base-000)')
      .style('font', '400 0.725rem "NotoSans"')

    svg
      .append('g')
      .selectAll('dot')
      .data(growthValue.values)
      .enter()
      .append('circle')
      .attr('cx', function (d) {
        return x(d.label) + x.bandwidth() / 1.5
      })
      .attr('cy', function (d) {
        return y2(d.value)
      })
      .attr('r', 5)
      .style('fill', 'var(--complementary-fourth)')

    const colorsByLabel = {
      P2: 'var(--complementary-second-150)',
      Growth: 'var(--complementary-fourth)'
    }

    const itemWidth = 80

    const legendGroup = svg
      .append('g')
      .attr(
        'transform',
        `translate(${width / 2 - itemWidth},${height - margin.bottom + 72})`
      )

    const legend = legendGroup
      .selectAll('.legend')
      .data(data.map((d) => d.label))
      .enter()
      .append('g')
      .attr('transform', function (d, i) {
        return `translate(${i * itemWidth},0)`
      })
      .attr('class', 'legend')

    legend
      .append('circle')
      .attr('cy', 8)
      .attr('cx', 6)
      .attr('r', 5)
      .attr('fill', function (d, i) {
        return colorsByLabel[d]
      })

    legend
      .append('text')
      .attr('x', 18)
      .attr('y', 12)
      .text(function (d) {
        return d
      })
      .attr('class', style.barScatterLegend)

    if (inView) {
      svg
        .selectAll('rect')
        .transition()
        .duration(800)
        .attr('y', (d) => y(d.value))
        .attr('height', (d) => y(0) - y(d.value))
        .delay(function (d, i) {
          return i * 100
        })
    }
  }, [inView])

  return (
    <>
      <div className="col-12">
        <h4 className="b200-bold text-base-000 text-center">{title}</h4>
        <div className="col-12" ref={ref} />
      </div>
    </>
  )
}

export default DataGraph
